import React, { useRef, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faImages, faScroll, faDna, faCube } from "@fortawesome/free-solid-svg-icons";
import CarouselVisCard from "../components/CarouselVisCard";
import CarouselPresentationCard from "../components/CarouselPresentationCard";
import CarouselExplainerCard from "../components/CarouselExplainerCard";
import "./NewHomePage.css";

// Example data - Replace these with actual vis IDs from your library
const exampleVisuals = {
  explainer: {
    id: "example-explainer-1",
    title: "AI-Generated Explainer Video",
    description: "Transform complex biomedical concepts into engaging video explainers with AI-powered narration and interactive 3D visualization.",
    thumbnail: "https://snorkle-output-files.s3.amazonaws.com/example-explainer-thumb.png",
    url: "#",
    qr_image: null,
    type: "explainer",
    favorite: false,
    folder_id: null
  },
  slideshow: {
    id: "example-slideshow-1",
    title: "Interactive Slide Deck",
    description: "Convert traditional slides into mobile-activating interactive experiences with QR code integration.",
    thumbnail: "https://snorkle-output-files.s3.amazonaws.com/example-slideshow-thumb.png",
    url: "#",
    qr_url: null,
    type: "slideshow",
    favorite: false,
    folder_id: null
  },
  scrollytelling: {
    id: "example-scrolly-1",
    title: "Scrollytelling Science Story",
    description: "Immersive narrative-driven visualizations that guide users through complex scientific concepts.",
    thumbnail: "https://snorkle-output-files.s3.amazonaws.com/example-scrolly-thumb.png",
    url: "#",
    qr_url: null,
    type: "scrollytelling",
    favorite: false,
    folder_id: null
  },
  molecular: {
    id: "example-molecular-1",
    title: "Interactive Molecular Visualization",
    description: "Embeddable 3D protein structures with residue highlighting and sequence tracking.",
    thumbnail: "https://snorkle-output-files.s3.amazonaws.com/example-molecular-thumb.png",
    visualization_url: "#",
    qr_image: null,
    favorite: false,
    folder_id: null,
    can_save_view: false,
    can_landmark: false,
    can_snapshot: false,
    downloadable: false
  },
  dicom: {
    id: "example-dicom-1",
    title: "3D Medical Imaging Segmentation",
    description: "Deep-learning powered DICOM segmentation with interactive 3D organ visualization.",
    thumbnail: "https://snorkle-output-files.s3.amazonaws.com/example-dicom-thumb.png",
    visualization_url: "#",
    qr_image: null,
    favorite: false,
    folder_id: null,
    can_save_view: false,
    can_landmark: false,
    can_snapshot: false,
    downloadable: false
  }
};

const examples = [
  {
    category: "Explainer Video",
    icon: faVideo,
    description: "Generate engaging explainer videos with AI-powered narration. Perfect for complex biomedical concepts, scientific outreach, and educational content. Simply describe what you want to explain, and Snorkle creates a complete video with professional voiceover in multiple languages.",
    visual: exampleVisuals.explainer,
    type: "explainer"
  },
  {
    category: "Slide Deck",
    icon: faImages,
    description: "Transform traditional presentations into interactive experiences. Convert PowerPoint slides into mobile-activating visuals with QR codes. Your audience gets the same control and inspection capabilities as the presenter, making every attendee an active participant.",
    visual: exampleVisuals.slideshow,
    type: "presentation"
  },
  {
    category: "Scrollytelling Story",
    icon: faScroll,
    description: "Create immersive narrative-driven visualizations. Guide your audience through complex scientific concepts with scroll-activated animations and interactive elements. Perfect for long-form scientific storytelling and data journalism.",
    visual: exampleVisuals.scrollytelling,
    type: "presentation"
  },
  {
    category: "Molecular Visualization",
    icon: faDna,
    description: "Embed interactive 3D molecular structures anywhere. Highlight individual residues, track positions in sequences, and narrate the story behind every molecular interaction. Created with our molecular viewer tool, these visuals make complex molecular structures accessible and engaging.",
    visual: exampleVisuals.molecular,
    type: "visual"
  },
  {
    category: "3D Medical Segmentation",
    icon: faCube,
    description: "AI-powered DICOM segmentation with interactive 3D visualization. Upload medical imaging data and our deep-learning tool automatically segments organs, creating interactive 3D models perfect for education, diagnosis explanation, and research collaboration.",
    visual: exampleVisuals.dicom,
    type: "visual"
  }
];

const ExamplesPage = () => {
  const starCanvasRef = useRef(null);
  const [dummyState, setDummyState] = useState(0); // For triggering re-renders on delete/edit

  useEffect(() => {
    // Canvas animation effect - same as homepage
    const canvas = starCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let stars = [];
    const handleResize = () => {
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const intendedDrawBufferWidth = window.innerWidth;
      const intendedDrawBufferHeight = window.innerHeight;
      canvas.width = intendedDrawBufferWidth * dpr;
      canvas.height = intendedDrawBufferHeight * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      stars = [];
      for (let i = 0; i < 200; i++) {
        stars.push({
          x: Math.random() * intendedDrawBufferWidth,
          y: Math.random() * intendedDrawBufferHeight,
          r: Math.random() * 1.5,
          dx: (Math.random() - 0.5) * 0.3,
          dy: (Math.random() - 0.5) * 0.3
        });
      }
    };
    let animationFrameId;
    function draw() {
      if (!ctx || !canvas) return;
      ctx.save();
      const currentDrawWidth = window.innerWidth;
      const currentDrawHeight = window.innerHeight;
      ctx.clearRect(0, 0, currentDrawWidth, currentDrawHeight);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      stars.forEach(s => {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, 2 * Math.PI);
        ctx.fill();
        s.x += s.dx;
        s.y += s.dy;
        if (s.x < 0) { s.x = currentDrawWidth + s.r; }
        else if (s.x > currentDrawWidth + s.r) { s.x = -s.r; }
        if (s.y < 0) { s.y = currentDrawHeight + s.r; }
        else if (s.y > currentDrawHeight + s.r) { s.y = -s.r; }
      });
      ctx.restore();
      animationFrameId = requestAnimationFrame(draw);
    }
    const initialResizeTimeoutId = setTimeout(() => {
      handleResize();
      draw();
    }, 0);
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(initialResizeTimeoutId);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Dummy handlers for the card components
  const handleVisDelete = (id) => {
    setDummyState(prev => prev + 1);
  };

  const handleVisEdit = (updatedVis) => {
    setDummyState(prev => prev + 1);
  };

  return (
    <div
      className="homepage-container"
      style={{
        width: '100%',
        position: 'relative',
        overflowX: 'hidden',
        background: 'linear-gradient(to bottom, #0a0f2c, #1b1f3a, #2d304d)',
      }}
    >
      <canvas
        id="stars"
        ref={starCanvasRef}
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}
      ></canvas>
      <div
        className="overlay"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.03), rgba(0,0,0,0.9))',
          pointerEvents: 'none',
          zIndex: 1
        }}
      ></div>

      <div style={{ position: 'relative', zIndex: 2 }}>
        {/* Hero Section */}
        <section className="scroll-section hero-section">
          <Container>
            <Row className="align-items-center justify-content-center text-center">
              <Col lg={10} md={12}>
                <h1>See What's Possible with Snorkle</h1>
                <p className="lead" style={{ color: "#cce7ff", marginTop: "2rem" }}>
                  Explore examples of interactive scientific visualizations created with Snorkle. From AI-generated explainers to 3D medical imaging, discover how Snorkle transforms complex science into engaging, explorable content.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Examples Panels */}
        {examples.map((example, index) => (
          <div className="use-case-panel" key={index}>
            <section className="scroll-section use-case-scroll-section">
              <Container className="scrolly-container">
                <div className="scrolly-text-content">
                  <div className="scrolly-text-block">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                      <FontAwesomeIcon 
                        icon={example.icon} 
                        style={{ 
                          fontSize: '2.5rem', 
                          color: '#00d8ff',
                          opacity: 0.9
                        }} 
                      />
                      <h2 style={{ margin: 0 }}>{example.category}</h2>
                    </div>
                    <p>{example.description}</p>
                  </div>
                </div>
                <div className="sticky-visual" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '2rem'
                }}>
                  <div style={{ 
                    maxWidth: '400px', 
                    width: '100%',
                    transform: 'scale(0.95)',
                    transition: 'transform 0.3s ease'
                  }}>
                    {example.type === "explainer" && (
                      <CarouselExplainerCard
                        explainer={example.visual}
                        onExplainerDelete={handleVisDelete}
                        onExplainerEdit={handleVisEdit}
                        isOrg={false}
                        folders={[]}
                        folderTree={[]}
                        lockoutEdit={true}
                        lockoutDelete={true}
                        blockActions={true}
                      />
                    )}
                    {example.type === "presentation" && (
                      <CarouselPresentationCard
                        presentation={example.visual}
                        onPresDelete={handleVisDelete}
                        onPresEdit={handleVisEdit}
                        isOrg={false}
                        folders={[]}
                        folderTree={[]}
                        lockoutEdit={true}
                        lockoutDelete={true}
                        blockActions={true}
                      />
                    )}
                    {example.type === "visual" && (
                      <CarouselVisCard
                        vis={example.visual}
                        onVisDelete={handleVisDelete}
                        onVisEdit={handleVisEdit}
                        isOrg={false}
                        folders={[]}
                        folderTree={[]}
                        lockoutEdit={true}
                        lockoutDelete={true}
                        blockActions={true}
                        blockVisLink={true}
                      />
                    )}
                  </div>
                </div>
              </Container>
            </section>
          </div>
        ))}

        {/* CTA Section */}
        <section className="scroll-section cta-section text-center" style={{ paddingBottom: '100px' }}>
          <Container>
            <h2 className="gradient-text">Ready to Create Your Own?</h2>
            <p className="lead" style={{ color: "#cce7ff", marginTop: "1.5rem", marginBottom: "2.5rem" }}>
              Join Snorkle and start transforming your science into interactive visualizations.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/dashboard" className="cta-button" style={{ textDecoration: 'none' }}>
                Get Started
              </a>
              <a href="/contact" className="cta-button" style={{ 
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(0, 216, 255, 0.5)'
              }}>
                Contact Us
              </a>
            </div>
          </Container>
        </section>
      </div>
    </div>
  );
};

export default ExamplesPage;

